import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import {
  LuSend,
  LuX,
  LuCircleAlert,
  LuCheck,
  LuRefreshCw,
  LuRotateCcw,
  LuBell,
} from "react-icons/lu";
import validator from "validator";
import { formatCurrencyAUD } from "utils/formatCurrency";
import QuotationRecipientField from "layouts/Quotation/QuotationRecipientField";
import {
  parseAdditionalQuotationEmails,
  validateAdditionalQuotationEmails,
} from "utils/quotationCustomerValidation";

function formatFigmaDate(dateVal) {
  if (!dateVal) return "—";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return String(dateVal);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (err) {
    return String(dateVal);
  }
}

function cleanHtmlToPlain(htmlStr) {
  if (!htmlStr) return "";
  return htmlStr
    .replace(/<a\b[^>]*\bhref\s*=\s*(["'])(.*?)\1[^>]*>([\s\S]*?)<\/a>/gi,
      (match, quote, href, label) => `${label} (${href})`)
    .replace(/<br\s*[/]?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export default function SendInvoiceModal({
  open,
  onClose,
  invoiceNumber = "",
  customerName = "Customer",
  customerEmail = "",
  customerEmails = [],
  invoiceDate,
  dueDate,
  currency = "AUD",
  finalAmount = 0,
  organisation: propOrganisation = "",
  invoiceStatus = "Saved draft",
  templateData = null,
  onSend,
  onViewInvoice,
  onEditRecipient,
  darkMode = false,
  mode = "send",
  documentLabel = "Invoice",
  messageOnly = false,
  confirmationText = "",
  sendLabel,
}) {
  const organisation = useMemo(() => {
    if (propOrganisation && propOrganisation !== "Maple Electrical") {
      return propOrganisation;
    }
    try {
      const user = JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}");
      return (
        user.companyName ||
        user.businessName ||
        user.organisationName ||
        user.organizationName ||
        user.fullName ||
        user.name ||
        `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
        "Invoice & Recover"
      );
    } catch {
      return "Invoice & Recover";
    }
  }, [propOrganisation]);

  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState("ready"); // 'ready' | 'sending' | 'failed' | 'success'
  const initializedForOpen = useRef(false);
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [initialAdditionalRecipients, setInitialAdditionalRecipients] =
    useState([]);
  const [additionalRecipients, setAdditionalRecipients] = useState("");
  const [recipientError, setRecipientError] = useState("");
  const [additionalRecipientsError, setAdditionalRecipientsError] =
    useState("");
  const [subjectError, setSubjectError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [sentRecipients, setSentRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [sendMeCopy, setSendMeCopy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const displayInvoiceNumber =
    invoiceNumber && invoiceNumber !== "Auto Generated"
      ? invoiceNumber
      : "Unsaved draft";

  const dueDateFormatted = formatFigmaDate(dueDate);

  const parseEmails = (input) => {
    const source = Array.isArray(input) ? input.join(",") : input;
    return String(source || "")
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
  };

  // Initialize once per opening so invoice refreshes preserve send confirmation.
  useEffect(() => {
    if (!open) initializedForOpen.current = false;
    if (open && !initializedForOpen.current) {
      initializedForOpen.current = true;
      setStatus("ready");
      setConfirmed(false);
      setErrorMessage("");

      const templateEmails = Array.isArray(templateData?.customerEmailAddress)
        ? templateData.customerEmailAddress
        : parseEmails(templateData?.customerEmailAddress);
      const suppliedEmails = Array.isArray(customerEmails)
        ? customerEmails
        : parseEmails(customerEmails);
      const rawEmails = templateEmails.length
        ? templateEmails
        : suppliedEmails.length
        ? suppliedEmails
        : parseEmails(customerEmail);
      const suppliedPrimaryEmails = parseEmails(customerEmail);
      const primary = String(
        suppliedPrimaryEmails[0] || rawEmails[0] || ""
      ).trim();
      const additionals = rawEmails
        .concat(suppliedPrimaryEmails.slice(1))
        .map((e) => String(e || "").trim())
        .filter(
          (e, index, emails) =>
            e &&
            e.toLowerCase() !== primary.toLowerCase() &&
            emails.findIndex(
              (candidate) => candidate.toLowerCase() === e.toLowerCase()
            ) === index
        );

      setPrimaryEmail(primary);
      setInitialAdditionalRecipients(additionals);
      setAdditionalRecipients(additionals.join(", "));

      const initialPrimaryError = primary
        ? validator.isEmail(primary)
          ? ""
          : "Please enter a valid email address, e.g. accounts@example.com."
        : "";
      setRecipientError(initialPrimaryError);
      setAdditionalRecipientsError("");
      setSubjectError("");
      setMessageError("");
      setSentRecipients([primary, ...additionals].filter(Boolean));

      const defaultSubject =
        templateData?.emailTemplate?.templateSubject ||
        `${documentLabel} ${displayInvoiceNumber} from ${organisation}`;
      const templateFields = {
        CustomerName: customerName,
        RCTINumber: displayInvoiceNumber,
        RCTIAmount: `${currency} ${formatCurrencyAUD(finalAmount)}`,
        RCTIDate: formatFigmaDate(invoiceDate),
        RCTIStatus: invoiceStatus,
        InvoiceNumber: displayInvoiceNumber,
        FinalAmount: `${currency} ${formatCurrencyAUD(finalAmount)}`,
        InvoiceDate: formatFigmaDate(invoiceDate),
        DueDate: dueDateFormatted,
      };
      const populateTemplate = (text) => String(text).replace(
        /\[(CustomerName|InvoiceNumber|FinalAmount|InvoiceDate|DueDate|RCTINumber|RCTIAmount|RCTIDate|RCTIStatus)\]/g,
        (match, field) => templateFields[field]
      );
      setSubject(populateTemplate(defaultSubject));

      const rawMessage = templateData?.emailTemplate?.templateMessage;
      if (rawMessage) {
        setMessage(populateTemplate(cleanHtmlToPlain(rawMessage)));
      } else if (documentLabel === "RCTI") {
        setMessage(
          `Hello ${customerName},\n\nPlease find RCTI ${displayInvoiceNumber} for $${formatCurrencyAUD(
            finalAmount
          )} ${currency} from ${organisation}. A PDF copy is attached, and you can view the RCTI using the private link included in the email.\n\nThanks,\n${organisation}`
        );
      } else {
        setMessage(
          `Hello ${customerName},\n\nPlease find invoice ${displayInvoiceNumber} for $${formatCurrencyAUD(
            finalAmount
          )} ${currency} from ${organisation}, due ${dueDateFormatted}. You can view the invoice details using the link below.\n\nThanks,\n${organisation}`
        );
      }

      setAttachPdf(true);
      setSendMeCopy(Boolean(templateData?.sendMeCopy));
    }
  }, [
    open,
    templateData,
    displayInvoiceNumber,
    customerName,
    customerEmail,
    customerEmails,
    invoiceDate,
    invoiceStatus,
    dueDateFormatted,
    finalAmount,
    currency,
    organisation,
    documentLabel,
  ]);

  if (!open) return null;

  const handleClose = () => {
    if (status === "sending") return; // prevent closing while in-flight
    onClose();
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const addlError = validateAdditionalQuotationEmails(additionalRecipients);
    setAdditionalRecipientsError(addlError);

    const primaryErr = primaryEmail
      ? validator.isEmail(primaryEmail)
        ? ""
        : "Please enter a valid email address, e.g. accounts@example.com."
      : "";
    setRecipientError(primaryErr);

    const nextSubjectError = subject.trim()
      ? ""
      : "Please enter an email subject.";
    const nextMessageError = message.trim()
      ? ""
      : "Please enter an email message.";
    setSubjectError(nextSubjectError);
    setMessageError(nextMessageError);

    if (primaryErr || addlError || nextSubjectError || nextMessageError) {
      setStatus("ready");
      setErrorMessage("");
      window.requestAnimationFrame(() => {
        document
          .querySelector(".send-invoice-dialog [aria-invalid='true']")
          ?.focus();
      });
      return;
    }

    const seen = new Set();
    const emails = [
      primaryEmail,
      ...parseAdditionalQuotationEmails(additionalRecipients),
    ]
      .map((e) => String(e || "").trim())
      .filter((e) => {
        if (!e) return false;
        const key = e.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    if (emails.length === 0) {
      setStatus("ready");
      setRecipientError(
        "Please enter at least one valid recipient email address."
      );
      document.getElementById("send-to-input")?.focus();
      return;
    }

    setSentRecipients(emails);
    setStatus("sending");
    setErrorMessage("");

    try {
      if (onSend) {
        const payload = {
          userEmailAddress:
            templateData?.userEmailAddress ||
            sessionStorage.getItem("USER_EMAIL") ||
            "",
          customerEmailAddress: emails,
          emailTemplate: {
            contentIsFinal: true,
            templateSubject: subject,
            templateMessage: message
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")
              .replace(/\r\n|\r|\n/g, "<br />"),
          },
          sendMeCopy,
          attachPdf,
          selectedID: templateData?.selectedID,
          screenType: templateData?.screenType || "Invoices",
        };

        const result = await onSend(payload);
        if (result && result.success === false) {
          setStatus("failed");
          setErrorMessage(
            result.message ||
              "No email sent and no payment recorded. Review the recipient address or try again."
          );
        } else {
          setStatus("success");
        }
      } else {
        setStatus("success");
      }
    } catch (err) {
      setStatus("failed");
      setErrorMessage(
        err?.message ||
          "No email sent and no payment recorded. Review the recipient address or try again."
      );
    }
  };

  const getHeaderTitle = () => {
    if (status === "sending") return `Sending ${documentLabel}…`;
    if (status === "failed") return `${documentLabel} not sent`;
    if (status === "success")
      return mode === "reminder" ? "Reminder sent" : `${documentLabel} sent`;
    if (mode === "resend") return `Resend ${documentLabel} ${displayInvoiceNumber}`;
    if (mode === "reminder") return "Send reminder";
    return `Send ${documentLabel} ${displayInvoiceNumber}`;
  };

  const getHeaderIcon = () => {
    if (status === "sending") return <LuRefreshCw className="send-icon spin" />;
    if (status === "failed")
      return <LuCircleAlert className="send-icon failed" />;
    if (status === "success") return <LuCheck className="send-icon success" />;
    if (mode === "resend") return <LuRotateCcw className="send-icon resend" />;
    if (mode === "reminder") return <LuBell className="send-icon reminder" />;
    return <LuSend className="send-icon" />;
  };

  return createPortal(
    <div
      style={messageOnly ? { zIndex: 1600 } : undefined}
      className={`send-invoice-dialog-backdrop${darkMode ? " dark" : ""}`}
      data-document-type={documentLabel}
      data-clickable={status !== "sending"}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={getHeaderTitle()}
    >
      <div
        className="send-invoice-dialog"
        data-clickable={false}
        onClick={(e) => e.stopPropagation()}
        data-status={status}
      >
        {/* Dialog Header */}
        <div className="send-dialog-header">
          <div className="header-identity">
            <span className="header-icon-wrap">{getHeaderIcon()}</span>
            <h2 className="header-title">{getHeaderTitle()}</h2>
          </div>
          <button
            type="button"
            className="send-dialog-close-btn"
            onClick={handleClose}
            disabled={status === "sending"}
            aria-label="Close dialog"
          >
            <LuX />
          </button>
        </div>

        <hr className="send-dialog-divider" />

        {/* Dialog Body */}
        <div className="send-dialog-body">
          {/* Sending State Notice */}
          {status === "sending" && (
            <div className="send-feedback-banner waiting">
              <p className="feedback-headline">Please wait for confirmation</p>
              <p className="feedback-sub">
                {documentLabel === "RCTI"
                  ? "Sending the saved RCTI. Duplicate submission is disabled."
                  : "The invoice remains a draft until sending succeeds. Duplicate submission is disabled."}
              </p>
            </div>
          )}

          {/* Failed State Notice */}
          {status === "failed" && (
            <div className="send-feedback-banner failed">
              <p className="feedback-headline">{documentLabel === "RCTI" ? "Email not sent" : "Sending failed · Draft saved"}</p>
              <p className="feedback-sub">
                {errorMessage ||
                  "No email sent and no payment recorded. Review the recipient address or try again."}
              </p>
            </div>
          )}

          {/* Success State Screen */}
          {status === "success" ? (
            <div className="send-success-panel">
              <div className="send-success-indicator">
                <span className="success-icon-badge">
                  <LuCheck />
                </span>
                <p className="success-sent-text">
                  {mode === "reminder" ? "Reminder" : documentLabel} sent
                  successfully to{" "}
                  {sentRecipients.join(", ") || primaryEmail || "customer"}
                </p>
              </div>

              <div className="success-invoice-identity font-mono">
                {displayInvoiceNumber} · {customerName}
              </div>

              <div className="success-badges">
                <span className="success-pill warning">Awaiting payment</span>
                <span className="success-pill info">Delivery: Sent</span>
              </div>

              <div className="success-financial-breakdown">
                <div className="breakdown-row">
                  <span>{documentLabel} total</span>
                  <strong className="font-mono">
                    ${formatCurrencyAUD(finalAmount)}
                  </strong>
                </div>
                <div className="breakdown-row">
                  <span>Payments received / Applied credits</span>
                  <strong className="font-mono">$0.00 / $0.00</strong>
                </div>
                <div className="breakdown-row">
                  <span>Remaining balance</span>
                  <strong className="font-mono">
                    ${formatCurrencyAUD(finalAmount)}
                  </strong>
                </div>
              </div>

              <p className="success-due-text">Due {dueDateFormatted}</p>
              <p className="success-footnote">
                Sending did not record payment or change the due date. Returns
                to invoice details with sent confirmation.
              </p>

              <div className="success-action-wrap">
                <button
                  type="button"
                  className="send-action-btn primary"
                  onClick={() => {
                    if (onViewInvoice) onViewInvoice();
                    else onClose();
                  }}
                >
                  View {documentLabel} details
                </button>
              </div>
            </div>
          ) : (
            /* Ready, Sending, or Failed States Form */
            <form onSubmit={handleSubmit} className="send-form-stack">
              <div className="send-context-row">
                <span className="context-identity font-mono">
                  {displayInvoiceNumber} · {customerName}
                </span>
                <span className="context-status-pill">
                  {invoiceStatus || "Saved draft"}
                </span>
              </div>

              <div
                className={`send-field-group${
                  status === "sending" ? " disabled" : ""
                }`}
              >
                {messageOnly ? (<>
                  <label>To</label><div className="send-input-box"><input readOnly value={`${customerName} <${primaryEmail}>`} /></div>
                  <label>From</label><div className="send-input-box"><input readOnly value={`${organisation} <${templateData?.userEmailAddress || ""}>`} /></div>
                </>) : <QuotationRecipientField
                  id="send-to-input"
                  primaryEmail={primaryEmail}
                  initialRecipients={initialAdditionalRecipients}
                  disabled={status === "sending"}
                  primaryError={recipientError}
                  additionalError={additionalRecipientsError}
                  onChange={(emailsStr, emailsArr) => {
                    setAdditionalRecipients(emailsStr);
                    setAdditionalRecipientsError("");
                    if (primaryEmail || emailsArr?.length)
                      setRecipientError("");
                    setErrorMessage("");
                  }}
                  onError={(err) => {
                    setAdditionalRecipientsError(err);
                    if (err) setErrorMessage(err);
                  }}
                  savedTitle="Saved invoice recipient"
                />}
                {recipientError && onEditRecipient && (
                  <button
                    type="button"
                    className="quotation-dialog-cancel-btn"
                    onClick={onEditRecipient}
                  >
                    Update recipient email in invoice
                  </button>
                )}
              </div>

              <div
                className={`send-field-group${
                  status === "sending" ? " disabled" : ""
                }`}
              >
                <label htmlFor="send-subject-input">Subject</label>
                <div className="send-input-box">
                  <input
                    id="send-subject-input"
                    type="text"
                    value={subject}
                    readOnly={messageOnly}
                    disabled={status === "sending"}
                    aria-invalid={Boolean(subjectError)}
                    aria-describedby={
                      subjectError ? "send-subject-error" : undefined
                    }
                    onChange={(e) => {
                      setSubject(e.target.value);
                      setSubjectError("");
                    }}
                    placeholder={`${documentLabel} ${displayInvoiceNumber} from ${organisation}`}
                  />
                </div>
                {subjectError && (
                  <span
                    id="send-subject-error"
                    className="send-field-error"
                    role="alert"
                  >
                    {subjectError}
                  </span>
                )}
              </div>

              <div
                className={`send-field-group${
                  status === "sending" ? " disabled" : ""
                }`}
              >
                <label htmlFor="send-message-textarea">Message</label>
                <div className="send-textarea-box">
                  <textarea
                    id="send-message-textarea"
                    rows={6}
                    value={message}
                    disabled={status === "sending"}
                    aria-invalid={Boolean(messageError)}
                    aria-describedby={
                      messageError ? "send-message-error" : undefined
                    }
                    onChange={(e) => {
                      setMessage(e.target.value);
                      setMessageError("");
                    }}
                    placeholder="Write a message to your customer…"
                  />
                </div>
                {messageError && (
                  <span
                    id="send-message-error"
                    className="send-field-error"
                    role="alert"
                  >
                    {messageError}
                  </span>
                )}
              </div>

              <div className="send-summary-card">
                <p className="summary-title">{documentLabel} link included in email</p>
                <p className="summary-detail">
                  {displayInvoiceNumber} · Total $
                  {formatCurrencyAUD(finalAmount)} {currency}
                  {documentLabel !== "RCTI" && <> · Due {dueDateFormatted}</>}
                </p>
              </div>

              <div
                className={`send-delivery-options${
                  status === "sending" ? " disabled" : ""
                }`}
              >
                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={attachPdf}
                    disabled={status === "sending"}
                    onChange={(e) => setAttachPdf(e.target.checked)}
                  />
                  <span>Attach PDF</span>
                </label>
                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={sendMeCopy}
                    disabled={status === "sending"}
                    onChange={(e) => setSendMeCopy(e.target.checked)}
                  />
                  <span>Send me a copy</span>
                </label>
              </div>

              <p className="send-supporting-copy">
                {documentLabel === "RCTI"
                  ? "Email a copy of this issued RCTI to the saved supplier recipients. Delivery is confirmed after successful submission."
                  : "Sending confirms this saved invoice and changes delivery to Sent and finance to Awaiting payment only after success."}
              </p>
              {confirmationText && (
                <label className="rcti-email-authorisation"><input type="checkbox" checked={confirmed} disabled={status === "sending"} onChange={(event) => setConfirmed(event.target.checked)} /><span>{confirmationText}</span></label>
              )}
            </form>
          )}
        </div>

        {/* Dialog Actions Footer (only for ready, sending, and failed states) */}
        {status !== "success" && (
          <>
            <hr className="send-dialog-divider" />
            <div className="send-dialog-actions">
              <button
                type="button"
                className="send-action-btn outline"
                disabled={status === "sending"}
                onClick={handleClose}
              >
                Cancel
              </button>


              {status === "ready" && (
                <button
                  type="button"
                  className="send-action-btn primary"
                  onClick={handleSubmit}
                  disabled={Boolean(confirmationText) && !confirmed}
                >
                  {sendLabel || (mode === "resend"
                    ? `Resend ${documentLabel}`
                    : mode === "reminder"
                    ? "Send reminder"
                    : `Confirm & send ${documentLabel}`)}
                </button>
              )}

              {status === "sending" && (
                <button
                  type="button"
                  className="send-action-btn primary sending"
                  disabled
                >
                  <LuRefreshCw className="spin" />
                  Sending…
                </button>
              )}

              {status === "failed" && (
                <button
                  type="button"
                  className="send-action-btn primary"
                  onClick={handleSubmit}
                  disabled={Boolean(confirmationText) && !confirmed}
                >
                  Retry send
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

SendInvoiceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invoiceNumber: PropTypes.string,
  customerName: PropTypes.string,
  customerEmail: PropTypes.string,
  customerEmails: PropTypes.arrayOf(PropTypes.string),
  invoiceDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  dueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  currency: PropTypes.string,
  finalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  organisation: PropTypes.string,
  invoiceStatus: PropTypes.string,
  templateData: PropTypes.object,
  onSend: PropTypes.func.isRequired,
  onViewInvoice: PropTypes.func,
  onEditRecipient: PropTypes.func,
  darkMode: PropTypes.bool,
  mode: PropTypes.oneOf(["send", "resend", "reminder"]),
};
